import React, { useState, useCallback, useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import { Select, AsyncSelect } from '../Select'
import parseAddress from 'parse-address-string'
import Icon from 'components/Icon'

const isVAT = /^[A-Za-z]{2,4}(?=.{2,12}$)[-_ 0-9]*(?:[a-zA-Z][-_ 0-9]*){0,2}$/

const PaymentBanking = ({ setCallback, match: { params: { supplierId } } }) => {
  const { formatMessage } = useIntl()
  const vatIdRef = useRef()
  const [country, setCountry] = useState(null)
  const [currency, setCurrency] = useState(null)
  const [state, setState] = useState({
    euvatid: '',
    vat_registered: false,
    company: '',
    city: '',
    zip: '',
    address: '',
    state: '',
    country: null,
    account_holder: '',
    account_branch_code: '',
    account_number: '',
    bic: '',
    iban: '',
    bank_address: '',
    bank_name: '',
  })

  const [errors, setErrors] = useState(
    Object.keys(state).reduce((memo, key) => {
      memo[key] = true
      return memo
    }, {})
  )

  useEffect(() => {
    fetch(`/rest/v2/supplier/${supplierId}?includeRelations=1`, { credentials: 'same-origin' }) 
      .then((response) => response.json())
      .then((supplier) => {
        setCountry({
          value: supplier.country.uid,
          label: supplier.country.name,
          euMember: Boolean(Number(supplier.currency.euMember)),
        })

        setCurrency({
          value: supplier.currency.uid,
          label: supplier.currency.iso,
        })

        setState({
          euvatid: supplier.euvatid || '',
          vat_registered: Boolean(Number(supplier.vat_registered)),
          company: supplier.company || '',
          city: supplier.city || '',
          zip: supplier.zip || '',
          address: supplier.address || '',
          state: supplier.state || '',
          account_holder: supplier.bank_account ? supplier.bank_account.account_holder : '',
          account_branch_code: supplier.bank_account ? supplier.bank_account.account_branch_code : '',
          account_number: supplier.bank_account ? supplier.bank_account.account_number : '',
          bic: supplier.bank_account ? supplier.bank_account.bic : '',
          iban: supplier.bank_account ? supplier.bank_account.iban : '',
          bank_address: supplier.bank_account ? supplier.bank_account.bank_address : '',
          bank_name: supplier.bank_account ? supplier.bank_account.bank_name : '',
        })
      })
      .catch((error) => {
        window.alert(error)
      })
  }, [])

  useEffect(() => {
    setCallback((event) => new Promise((resolve, reject) => {
      const nextErrors = {
        ...errors,
        address: !!state.address && state.address.trim().length > 3,
        city: !!state.city && state.city.trim().length > 1,
        zip: !!state.zip && state.zip.trim().length > 1,
        company: !!state.company && state.company.trim().length > 2,
        country: !!country.value,
        account_holder: state.account_holder.length > 2,
      }

      if (country.euMember && country.label !== 'Denmark') {
        nextErrors.euvatid = !!state.euvatid.length ? isVAT.test(state.euvatid) : true
        nextErrors.iban = !!state.iban && state.iban.length > 0
        nextErrors.bic = !!state.bic && state.bic.length >= 8 && state.bic.length <= 11
        nextErrors.bank_name = !!state.bank_name

        nextErrors.account_branch_code = true
        nextErrors.account_number = true
        nextErrors.bank_address = true
      } else {
        nextErrors.account_branch_code = state.account_branch_code.length > 0
        nextErrors.account_number = state.account_number.length > 5
        nextErrors.bank_name = !!state.bank_name

        nextErrors.euvatid = true
        nextErrors.iban = true
        nextErrors.bic = true

        if (country.label === 'Denmark') {
          nextErrors.bank_address = true
        } else {
          nextErrors.bank_address = !!state.bank_address
        }
      }

      if (Object.values(nextErrors).some((valid) => !valid)) {
        event.stopPropagation()
        event.preventDefault()

        setErrors(nextErrors)

        return reject(formatMessage(messages.validationFailed))
      }

      fetch(`/rest/v2/supplier/${supplierId}`, {
        credentials: 'same-origin',
        method: 'PUT',
        body: JSON.stringify({
          country: country.value,
          currency: currency.value,

          euvatid: state.euvatid,
          vat_registered: Number(state.vat_registered),
          company: state.company,
          city: state.city,
          zip: state.zip,
          address: state.address,
          state: state.state,
          bank_account: {
            account_holder: state.account_holder,
            account_branch_code: state.account_branch_code,
            account_number: state.account_number,
            bic: state.bic,
            iban: state.iban,
            bank_address: state.bank_address,
            bank_name: state.bank_name,
          }
        }),
      })
        .then(resolve)
        .catch(reject)
    }))
  }, [state, country, currency, errors])

  const onVatChange = useCallback(() => {
    if (!isVAT.test(state.euvatid)) {
      return
    }

    const params = new URLSearchParams(Object.entries({
      country: state.euvatid.substring(0, 2),
      vat_id: state.euvatid.substring(2),
    }))

    fetch(`/system/vies?${params}`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then(({ valid, name, address }) => {
        if (valid) {
          parseAddress(address, (err, addr) => {
            setState((state) => ({
              ...state,
              address: addr.street_address1,
              state: addr.state,
              zip: addr.postal_code,
              city: addr.city,
              company: name
            }))
          })
        }
      })
      .catch(() => {}) // No op
  }, [state.euvatid])

  const removeSpaces = (key) => {
    setState({ ...state, [key]: state[key].replace(' ', '') })
  }

  const renderInput = useCallback((name, className = "block mt-6", required = true, onBlur = undefined) => {
    return (
      <fieldset className="block">
        <p className="py-4">
          <FormattedMessage {...messages[`${name}Label`] } /> { required && '*' }
          { (messages.hasOwnProperty(`${name}Info`)) &&
            <Icon
              icon="info-circle"
              title={formatMessage(messages[`${name}Info`])}
              className="text-primary hover:text-secondary ml-4"
            />
        }
        </p>

        <input
          name={name}
          type="text"
          value={state[name]}
          onChange={({ target: { value } }) => setState({ ...state, [name]: value })}
          onBlur={(event) => {
            setErrors((prevErrors) => ({ ...prevErrors, [name]: true }))
            if (onBlur) {
              onBlur(event)
            }
          }}
          placeholder={formatMessage(messages[`${name}Placeholder`])}
          className={`w-full p-4 focus:border-primary transition duration-200 border ${required && !errors[name] ? 'border-red-600' : 'border-gray-400'}`}
        />
        </fieldset>
    )
  }, [state, errors])

  return (
    <div>
      <div className="font-medium text-4xl">
        <FormattedMessage {...messages.title } />
      </div>

      <div className="text-gray-700 mt-6 w-2/3">
        <FormattedMessage
          {...messages.subtitle }
          values={{
            link: (url) => <a className="text-primary hover:text-primary-darker cursor-pointer" href={url} target="_blank">{ url }</a>
          }}
        />
      </div>

      <div className="flex space-x-10">
        <div className="w-1/2">
          <div>{ renderInput('euvatid', null, (country?.euMember && country?.label !== 'Denmark') || false, onVatChange) }</div>
          <div className="mt-2">
            <input
              className="mr-1"
              type="checkbox"
              onChange={({ target: { checked } }) => setState({ ...state, vat_registered: checked })}
              checked={Boolean(Number(state.vat_registered))}
            />
            <FormattedMessage {...messages.vatRegistered } />
          </div>
        </div>
        <div className="w-1/2">{ renderInput('company') }</div>
      </div>

      <div className="mt-20">{ renderInput('address') }</div>

      <div className="flex space-x-10">
        <div className="w-1/2">{ renderInput('city') }</div>
        <div className="w-1/2">{ renderInput('zip') }</div>
      </div>

      <div className="flex space-x-10">
        <div className="w-1/2">{ renderInput('state', null, false) }</div>
        <div className="w-1/2">
          <fieldset className="block">
            <p className="py-4">
              <FormattedMessage {...messages.countryLabel } /> *
            </p>
            <AsyncSelect
              value={country}
              onChange={setCountry}
              isValid={errors.country}
              onBlur={() => setErrors((prevErrors) => ({ ...prevErrors, country: true }))}
              isMulti={false}
              placeholder={formatMessage(messages.countryLabel)}
              noOptionsMessage={({ inputValue }) => `No Country found for '${inputValue}'`}
              promise={
                (query) => fetch(`/rest/v2/country?search[name]=${query}`, { credentials: 'same-origin' })
                  .then((response) => response.json())
                  .then((countries) => countries.map(({ uid, name, euMember }) => ({ value: uid, label: name, euMember: Boolean(Number(euMember)) })))
              }
            />
          </fieldset>
        </div>
      </div>

      <div className="mt-20 pb-20">
        { renderInput('account_holder', null, true) }

        <div className="flex space-x-10">
          <div className="w-1/2 space-x-10 flex">
            <fieldset className="block w-1/2">
              <p className="py-4">
                <FormattedMessage {...messages.currencyLabel } /> *
              </p>
              <Select
                value={currency}
                onChange={setCurrency}
                isMulti={false}
                placeholder={formatMessage(messages.currencyPlaceholder)}
                options={[
                  { label: 'USD', value: 1 },
                  { label: 'EUR', value: 2 },
                  { label: 'DKK', value: 3 },
                  { label: 'GBP', value: 4 },
                  { label: 'NOK', value: 5 },
                  { label: 'SEK', value: 6 },
                  { label: 'ZAR', value: 58 },
                ]}
              />
            </fieldset>

            <div className="w-1/2">{ renderInput('account_branch_code', null, !(country?.euMember && country?.label !== 'Denmark'), () => removeSpaces('account_branch_code')) }</div>
          </div>
          <div className="w-1/2">{ renderInput('account_number', null, !(country?.euMember && country?.label !== 'Denmark'), () => removeSpaces('account_number')) }</div>
        </div>

        { renderInput('bic', null, (country?.euMember && country?.label !== 'Denmark') || false, () => removeSpaces('bic')) }
        { renderInput('iban', null, (country?.euMember && country?.label !== 'Denmark') || false, () => removeSpaces('iban')) }
        { renderInput('bank_name', null, true) }
        { renderInput('bank_address', null, !country?.euMember) }
      </div>
    </div>
  )
}

export default PaymentBanking
